import { initConfig } from 'APP/Lib/Initializers'
import * as NativeCall from 'APP/NativeModules/NativeCall'
import { take } from 'redux-saga/effects'
import { call, put, select } from 'typed-redux-saga'
import { videoSessionActions } from '../VideoSession'
import { onDemandCallRequested, waitForAppHydrated } from '../VideoSession/sagas'
import { waitForNavigationReady } from 'APP/Sagas/StartupSagas'
import type { PayloadAction } from '@reduxjs/toolkit'
import type {
  CallAcceptedPayload,
  CallEndedPayload,
  IncomingFullScreenCallPayload,
  NotificationCallAcceptedPayload,
  NotificationCallEndedPayload,
  ShowOngoingCallNotificationPayload,
} from 'APP/Store/NativeCallAndroid/types'
import { logger } from 'APP/Lib/Datadog'
import { nativeCallAndroidActions, selectNativeCallAndroid } from './slice'
import Analytics from 'APP/Services/Analytics'

export function* incomingFullScreenCall(action: PayloadAction<IncomingFullScreenCallPayload>) {
  const { sessionId } = action.payload
  yield* call(waitForAppHydrated)
  yield* call(waitForNavigationReady)

  logger.info('Running Android fullscreen incoming call flow', {
    onDemandSessionId: sessionId,
  })

  yield* call(onDemandCallRequested, { payload: { useFullScreen: true, sessionId } })
}

export function* callAccepted(action: PayloadAction<CallAcceptedPayload>) {
  const { sessionId } = action.payload
  logger.info('callAccepted saga started', { onDemandSessionId: sessionId })

  yield* put(nativeCallAndroidActions.showOngoingCallNotification({ sessionId }))
}

export function* callEnded(action: PayloadAction<CallEndedPayload>) {
  const { sessionId: onDemandSessionId } = action.payload
  const { sessionId: storeSessionId } = yield* select(selectNativeCallAndroid)

  logger.info('[Android] callEnded saga started', { onDemandSessionId, storeSessionId })

  if (onDemandSessionId && onDemandSessionId !== storeSessionId) {
    logger.warn(
      '[Android] onDemandSessionId does not match value from store, no effects will be run'
    )
    return
  }

  yield* put(nativeCallAndroidActions.dismissVideoCall())
}

export function* dismissVideoCall() {
  logger.info('[Android] dismissVideoCall saga started')
  yield* call(NativeCall.dismissCallNotification)
}

export function* notificationCallAccepted(action: PayloadAction<NotificationCallAcceptedPayload>) {
  // This can be received before the app is ready or the call is received.
  yield* call(waitForAppHydrated)
  yield* call(waitForNavigationReady)

  // Accept the call.
  const { sessionId } = action.payload
  const lastCallAcceptedAt = Date.now()

  logger.info('Patient accepted call from Android notification', {
    sessionId,
  })

  Analytics.trackEvent('video_call_accepted', {
    session_id: sessionId,
    source: 'native_call_android',
  })

  yield* put(
    videoSessionActions.patientAcceptedCall({
      lastCallAcceptedAt,
      onDemandSessionId: sessionId,
    })
  )
}

export function* notificationCallEnded(action: PayloadAction<NotificationCallEndedPayload>) {
  const { declined, sessionId: onDemandSessionId } = action.payload

  logger.info('notificationCallEnded saga started', {
    declined,
    onDemandSessionId,
  })
  // This can be received before the app is ready or the call is received.
  // This might also be called in headless mode, where nothing is initialized.
  yield* call(initConfig)
  yield* call(waitForAppHydrated)

  if (declined) {
    Analytics.trackEvent('video_call_declined', {
      session_id: onDemandSessionId,
      source: 'native_call_android',
    })
    yield* put(videoSessionActions.patientDeclinedCall({ onDemandSessionId }))
    return
  }

  Analytics.trackEvent('video_call_ended', {
    session_id: onDemandSessionId,
    source: 'native_call_android',
  })

  yield* put(
    videoSessionActions.patientEndedCall({
      declined,
      onDemandSessionId,
    })
  )
}

export function* showOngoingCallNotification(
  action: PayloadAction<ShowOngoingCallNotificationPayload>
) {
  const { sessionId } = action.payload
  logger.info('[Android] showOngoingCallNotification saga started', { sessionId })

  yield* call(NativeCall.showOngoingCallNotification, sessionId)
}

export function* waitForCallDismissed(callback: () => void) {
  yield take(videoSessionActions.videoCallDismissed)
  callback()
}
